/**
 * OmniBar
 *
 * Unified search bar component that replaces IngredientSearchSection.
 * Provides search, focus states, clear button, AI sparkle indicator, and cancel button.
 */

import React, { useRef, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Platform,
  Text,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
} from '../../styles/theme';

interface OmniBarProps {
  value: string;
  onChangeText: (text: string) => void;
  onFocus: () => void;
  onBlur?: () => void;
  onCancel: () => void;
  onSubmitEditing?: () => void;
  focused: boolean;
  placeholder?: string;
  /**
   * The Cancel button leaves search mode on the Recipes tab. A panel with its
   * own close button (the planner's Library) has no search mode to leave.
   */
  showCancel?: boolean;
  testID?: string;
}

export const OmniBar: React.FC<OmniBarProps> = ({
  value,
  onChangeText,
  onFocus,
  onBlur,
  onCancel,
  onSubmitEditing,
  focused,
  placeholder,
  showCancel = true,
  testID,
}) => {
  const { t } = useTranslation();
  const textInputRef = useRef<TextInput>(null);
  // Focused, the bar gives ~55pt to the cancel button and the long prompt no
  // longer fits — it truncated mid-word ("…demander à Papr"). A focused field
  // does not need to explain itself, so it drops to the short form.
  const displayPlaceholder =
    placeholder ??
    t(focused ? 'recipes.omnibar.placeholderFocused' : 'recipes.omnibar.placeholder');

  // Sync focus prop with TextInput
  useEffect(() => {
    if (focused) {
      textInputRef.current?.focus();
    } else {
      textInputRef.current?.blur();
    }
  }, [focused]);

  const handleClear = () => {
    onChangeText('');
  };

  // Determine colors based on focus state
  const searchIconColor = focused ? Paprika.primary : Paprika.textMuted;
  const backgroundColor = focused ? '#ffffff' : Paprika.bgSunk;
  const borderColor = focused ? Paprika.primary : 'transparent';

  return (
    <View style={styles.outerContainer}>
      {/* Focus ring outer wrapper (iOS visual shadow) */}
      <View
        style={[
          styles.focusRingWrapper,
          focused && styles.focusRingWrapperActive,
        ]}
      >
        <View
          style={[
            styles.barContainer,
            focused && styles.barContainerFocused,
          ]}
        >
          {/* Left search icon */}
          <Feather
            name="search"
            size={18}
            color={searchIconColor}
            style={styles.searchIcon}
          />

          {/* Text input */}
          <TextInput
            ref={textInputRef}
            testID={testID ?? 'omnibar-input'}
            style={[
              styles.textInput,
              {
                color: value ? Paprika.textPrimary : Paprika.textMuted,
              },
            ]}
            placeholder={displayPlaceholder}
            placeholderTextColor={Paprika.textMuted}
            value={value}
            onChangeText={onChangeText}
            onFocus={onFocus}
            onBlur={onBlur}
            returnKeyType="search"
            onSubmitEditing={onSubmitEditing}
            autoCapitalize="none"
            autoCorrect={false}
          />

          {/* Right side: Clear button (if focused and has text) or AI sparkle (if unfocused) */}
          {focused && value ? (
            <TouchableOpacity
              onPress={handleClear}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              style={styles.clearButtonHitArea}
            >
              <Feather name="x" size={18} color={Paprika.primary} />
            </TouchableOpacity>
          ) : !focused ? (
            <View style={styles.aiSparkleButton}>
              <Feather name="zap" size={16} color={Paprika.ai} />
            </View>
          ) : null}
        </View>
      </View>

      {/* Cancel button (visible when focused, positioned on the right) */}
      {focused && showCancel && (
        <TouchableOpacity
          onPress={onCancel}
          style={styles.cancelButton}
          testID="omnibar-cancel"
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <View>
            <Text style={styles.cancelButtonText}>
              {t('recipes.search.cancel')}
            </Text>
          </View>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: PaprikaSpacing.md,
  },

  focusRingWrapper: {
    flex: 1,
    // A flex item wrapping an <input> gets `min-width: auto` on web, which
    // resolves to the field's min-content width and stops the bar shrinking.
    // Not what caused the overflow above, but it is the reason a narrower
    // phone or a longer locale would.
    minWidth: 0,
    borderRadius: 15,
    // iOS: visual shadow layer for focus ring
    ...Platform.select({
      ios: {
        shadowColor: 'transparent',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0,
        shadowRadius: 0,
      },
      android: {
        // Android uses elevation directly on barContainer
      },
      default: {},
    }),
  },

  focusRingWrapperActive: {
    // React Native equivalent of `box-shadow: 0 0 0 4px rgba(154,79,37,0.10)` —
    // a spread-only shadow is a border in RN; negative margin prevents layout shift.
    borderWidth: 4,
    borderColor: 'rgba(154, 79, 37, 0.10)',
    margin: -4,
  },

  barContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 48,
    borderRadius: 15,
    paddingLeft: 15,
    paddingRight: 7,
    backgroundColor: Paprika.bgSunk,
    borderWidth: 1.5,
    borderColor: 'transparent',
    ...Platform.select({
      android: {
        elevation: 0, // No elevation when unfocused
      },
    }),
  },

  barContainerFocused: {
    backgroundColor: '#ffffff',
    borderColor: Paprika.primary,
    ...Platform.select({
      android: {
        elevation: 2, // Elevation when focused
      },
    }),
  },

  searchIcon: {
    marginRight: PaprikaSpacing.sm,
  },

  textInput: {
    flex: 1,
    // 16, not 15. iOS Safari zooms the whole page on focus whenever a form
    // field renders below 16px, and public/index.html deliberately sets no
    // `maximum-scale` (that would also block pinch-zoom). At 15px the zoom is
    // 16/15 ≈ 1.067×, which pushed "Annuler" and the header avatar off the
    // right edge — measured against a device screenshot, not theorised.
    // public/index.html now floors every field at 16px on touch screens
    // (#723); this keeps the search bar laid out for the size it renders at.
    fontSize: 16,
    fontFamily: PaprikaTypography.bodyMedium.fontFamily,
    paddingVertical: 0,
    paddingHorizontal: 0,
    ...Platform.select({
      // RNW rejects the "outline" shorthand — use the long-form property.
      web: { outlineStyle: 'none' } as object,
    }),
  },

  clearButtonHitArea: {
    padding: PaprikaSpacing.xs,
    marginLeft: PaprikaSpacing.xs,
  },

  aiSparkleButton: {
    width: 36,
    height: 36,
    borderRadius: 11,
    backgroundColor: Paprika.aiSoft,
    justifyContent: 'center',
    alignItems: 'center',
  },

  cancelButton: {
    padding: PaprikaSpacing.xs,
    // Without this the label is what the row squeezes first, so "Annuler"
    // clips before the bar gives up a single point.
    flexShrink: 0,
  },

  cancelButtonText: {
    fontSize: 14.5,
    fontWeight: '600',
    color: Paprika.primary,
    fontFamily: PaprikaTypography.labelLarge.fontFamily,
  },
});

export default OmniBar;
